import { StepCard, StepActions } from '../../Styles/StepLayout';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, CardContent, FormControl, FormControlLabel, Radio, RadioGroup, Typography, Alert } from '@mui/material';
import StyledButton from '../../Styles/StyledButton';
import Title from '../Shared/Title';
import { GetCatchRights } from '../../Services/MillifaersluAPIService';
import CatchRights from '../../Models/Interfaces/CatchRights';
import { useYear } from '../../Contexts/YearContext';
import { useIsLoadingCatchRights } from '../../Contexts/IsLoadingCatchRightsContext';
import { useSelectedCatchRights } from '../../Contexts/QuotaSelectionContext';

interface YearSelectionProps {
    lastFishYear: string;
    currentFishYear: string;
    activeStep: number;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    setQuotaSelection: React.Dispatch<React.SetStateAction<CatchRights[] | null>>;
    fromShipNumber: number;
}

const YearSelection: React.FC<YearSelectionProps> = ({ lastFishYear, currentFishYear, setActiveStep, activeStep, setQuotaSelection, fromShipNumber }) => {
    const { currentYear, setCurrentYear } = useYear();
    const { isLoadingCatchRights, setIsLoadingCatchRights } = useIsLoadingCatchRights();
    const { setSelectedCatchRights } = useSelectedCatchRights();
    const [error, setError] = useState<string | null>(null);
    const catchRightsRequestId = useRef(0);

    useEffect(() => () => {
        catchRightsRequestId.current += 1;
        setIsLoadingCatchRights(false);
    }, [setIsLoadingCatchRights]);
    
    const handleYearSelection = (value: string) => {
        setCurrentYear(value === currentFishYear);
        void fetchCatchRights(fromShipNumber, value === currentFishYear);
    };

    const fetchCatchRights = async (shipNumber: number, currentYear: boolean) => {
        const requestId = ++catchRightsRequestId.current;
        setIsLoadingCatchRights(true);
        setError(null);
        setSelectedCatchRights([]);
        setQuotaSelection(null);
        try {
            const catchRights = await GetCatchRights(shipNumber, currentYear);
            if (requestId === catchRightsRequestId.current) {
                setQuotaSelection(catchRights);
            }
        } catch (err) {
            if (requestId === catchRightsRequestId.current) {
                setError('Ekki tókst að sækja aflaheimildir frá miðlara. Vinsamlegast reyndu aftur síðar.');
            }
        } finally {
            if (requestId === catchRightsRequestId.current) {
                setIsLoadingCatchRights(false);
            }
        }
    };

    const getInfoText = (value: string) => {
        if (value === lastFishYear) {
            return 'Ef valið er fyrrverandi fiskveiðiár verður millifærslan gerð á því fiskveiðiári.';
        } else if (value === currentFishYear) {
            return 'Ef valið er núverandi fiskveiðiár verður millifærslan gerð á því fiskveiðiári.';
        }
        return '';
    };

    const renderRadioCard = (value: string, label: string) => (
        <Card
            variant="outlined"
            sx={{
                cursor: 'pointer', border: 2,
                borderColor: currentYear === (value === currentFishYear) ? 'primary.main' : 'divider',
                bgcolor: currentYear === (value === currentFishYear) ? 'primary.light' : 'background.paper',
                '&:hover': { borderColor: 'primary.main' },
                '&:focus-within': { outline: '3px solid', outlineColor: 'secondary.main', outlineOffset: 2 },
            }}
        >
            <FormControlLabel
                value={value}
                control={<Radio />}
                disableTypography
                sx={{ m: 0, p: 3, width: '100%', height: '100%', alignItems: 'flex-start', gap: 1, '& .MuiRadio-root': { p: 0, mt: 0.25 } }}
                label={
                    <Box component="span" sx={{ minWidth: 0 }}>
                        <Typography component="span" variant="h6" sx={{ display: 'block', mb: 1 }}>{label}</Typography>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {getInfoText(value)}
                        </Typography>
                    </Box>
                }
            />
        </Card>
    );

    return (
        <StepCard>
            <Title title="Velja fiskveiðiár millifærslu " />
            <CardContent sx={{ p: 0, flexGrow: 1, overflow: 'auto' }}>
                {error && <Alert severity="error"  sx={{ marginBottom: '10px' }}>{error}</Alert>}
                <FormControl component="fieldset" fullWidth>
                    <RadioGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}
                        aria-label="Fiskveiðiár"
                        value={currentYear ? currentFishYear : lastFishYear}
                        onChange={(_, value) => handleYearSelection(value)}
                        name="radio-buttons-group"
                    >
                        {renderRadioCard(lastFishYear, lastFishYear)}
                        {renderRadioCard(currentFishYear, currentFishYear)}
                    </RadioGroup>
                </FormControl>
            </CardContent>
            <StepActions>
                <StyledButton
                    variant="outlined"
                    onClick={() => setActiveStep(activeStep - 1)}
                >
                    Til baka
                </StyledButton>
                <StyledButton
                    variant="contained"
                    onClick={() => setActiveStep(activeStep + 1)}
                    disabled={isLoadingCatchRights || !!error}
                >
                    Áfram
                </StyledButton>
            </StepActions>
        </StepCard>
    );
};

export default YearSelection;
